/* eslint-disable no-throw-literal */
/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useImmer } from "use-immer";
import { AppContext, AuthContext } from "../../../store";
import { useDropdownApis } from "../../../shared";
import { useLocation } from "react-router-dom";
import { getPlAndBsDashboardDetailsApi } from "../apis";
import { arraySort, errorMessage } from "../../../utils";
import moment from "moment";
import SimpleReactValidator from "simple-react-validator";

export const usePLDashboardList = () => {
  const {
    appState: { globalData },
  } = React.useContext(AppContext);
  const [, forceUpdate] = React.useState();
  const { auth } = React.useContext(AuthContext);

  const [state, setState] = useImmer({
    initialLoad: false,
    charts: {},
    filter: {
      options: {
        stores: [],
        financialYears: [],
        accountMonths: [],
        ratios: [],
        states: [],
      },
      values: {
        store: [],
        financialYear: null,
        month: [],
        ratio: [],
        state: [],
      },
      isFilterApplied: true,
    },
    chartLoading: true,
  });

  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("store-admin");

  const { franchiseeWiseStoreList, statesList } = useDropdownApis({
    isFranchiseeWiseStoreList: !isStoreAdmin,
    isStatesList: !isStoreAdmin,
  });

  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (msg) => msg,
      validators: {
        storeOrState: {
          message: "Select either stores or locations, not both",
          rule: ({ store, state }) => {
            const valid = Boolean(state.length) !== Boolean(store.length);
            return valid;
          },
        },
      },
    })
  );

  // ================================================================ API SECTION ======================================================
  const getPlAndBsDashboardDetails = async (formData) => {
    triggerChartLoading(true);
    try {
      const response = await getPlAndBsDashboardDetailsApi(formData);

      const { success, data, message } = response;

      if (success) {
        setState((draft) => {
          if (Object.entries(data).length > 0) {
            draft.charts = data;
          }
          if (state.initialLoad) {
            draft.filter.isFilterApplied = true;
          }
        });
      } else {
        throw { response: { data: { message } } };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ?? "Failed to fetch dashboard details";

      errorMessage(message);
    } finally {
      triggerChartLoading(false);
    }
  };

  // ================================================================ UTIL FUNCTIONS SECTION ======================================================
  const triggerChartLoading = (status) => {
    setState((draft) => {
      draft.chartLoading = status;
    });
  };

  const handleChangeFilter = ({ name, value }) => {
    setState((draft) => {
      if (name === "month") {
        const isYearly =
          Array.isArray(value) &&
          value.find((month) => month?.value === "Yearly");
        if (isYearly) {
          draft.filter.values[name] = isYearly ? [isYearly] : value;
        } else {
          const selectedQuarter = [];
          value.forEach((month) => {
            if (
              ["Quarter_1", "Quarter_2", "Quarter_3", "Quarter_4"].includes(
                month.value
              )
            ) {
              selectedQuarter.push(month.value);
            }
          });
          draft.filter.values[name] =
            selectedQuarter.length > 0
              ? value.filter(
                  (month) => !selectedQuarter.includes(month.quarter)
                )
              : value;
        }
      } else {
        draft.filter.values[name] = value;
      }
      draft.filter.isFilterApplied = false;
    });
  };

  const getFilterParams = async (filterValues) => {
    let formData = {};

    Object.entries(filterValues).forEach(([key, values]) => {
      const actualKey =
        key === "financialYear"
          ? "year"
          : key === "store"
          ? "store_id"
          : key === "ratio"
          ? "ratios"
          : key === "state"
          ? "state_id"
          : key;

      if (key === "month") {
        formData = {
          ...formData,
          [actualKey]: values.map((month) => month.value),
        };
      } else {
        formData = {
          ...formData,
          [actualKey]: Array.isArray(values)
            ? values.map((v) => v.value)
            : values?.value,
        };
      }
      formData[actualKey] = Array.isArray(values)
        ? values.map((value) => value.value)
        : [values.value];
    });

    if (isStoreAdmin && auth?.data?.selectedStoreId) {
      formData.store_id = [auth.data.selectedStoreId];
    }

    return formData;
  };

  const handleApplyFilter = async () => {
    if (validator.current.allValid()) {
      resetChartData();
      const formData = await getFilterParams(state.filter.values);
      await getPlAndBsDashboardDetails(formData);
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  function getChartTitle(key) {
    const ratio = state.filter.options.ratios.find(
      (ratio) => ratio.value?.toLowerCase() === key?.toLowerCase()
    );
    return ratio ? ratio.label : "";
  }

  const resetChartData = () => {
    setState((draft) => {
      draft.charts = {};
    });
  };

  React.useEffect(() => {
    if (!isStoreAdmin) {
      const stores = [
        ...franchiseeWiseStoreList.map((store, index) => ({
          label: store.name,
          value: store.id,
          franchisee_id: store.franchisee_id,
          franchisee_name: store.franchisee_name,
        })),
      ];
      setState((draft) => {
        if (franchiseeWiseStoreList.length > 0) {
          const sortedArray = arraySort(stores, "franchisee_name");
          draft.filter.options.stores = sortedArray;
          draft.filter.values.store = sortedArray;
        }
      });
    }
  }, [franchiseeWiseStoreList]);

  React.useEffect(() => {
    if (!isStoreAdmin) {
      setState((draft) => {
        if (statesList.length > 0) {
          const formattedStates = statesList.map((state) => ({
            label: state.name,
            value: state.id,
          }));

          draft.filter.options.states = formattedStates;
        }
      });
    }
  }, [statesList]);

  React.useEffect(() => {
    setState((draft) => {
      const accountRatios = globalData?.pl_bs_reports?.pl_account_ratios;
      const accountMonths = globalData?.pl_bs_reports?.pl_bs_accounts_months;
      const accountYears = globalData?.pl_bs_reports?.pl_bs_accounts_years;

      if (accountRatios) {
        draft.filter.options.ratios = accountRatios;
        draft.filter.values.ratio = accountRatios;
      }

      // Account Months
      if (accountMonths) {
        const months = accountMonths.map((month) => {
          const updatedMonth = {
            ...month,

            quarter:
              month.value <= 3
                ? "Quarter_1"
                : month.value <= 6
                ? "Quarter_2"
                : month.value <= 9
                ? "Quarter_3"
                : "Quarter_4",
          };
          if (month.label.includes(moment().format("MMMM"))) {
            draft.filter.values.month = [updatedMonth];
          }
          return updatedMonth;
        });

        draft.filter.options.accountMonths = [
          { label: "Yearly", value: "Yearly" },
          { label: "Quarter 1 (Jul - Sep)", value: "Quarter_1" },
          { label: "Quarter 2 (Oct - Dec)", value: "Quarter_2" },
          { label: "Quarter 3 (Jan - Mar)", value: "Quarter_3" },
          { label: "Quarter 4 (Apr - Jun)", value: "Quarter_4" },
        ].concat(months);
      }

      if (accountYears) {
        const currentFinancialYear = accountYears.find(
          (year) => year.value.split("-")[1] === moment().format("YYYY")
        );
        if (currentFinancialYear) {
          draft.filter.values.financialYear = currentFinancialYear;
        }
        draft.filter.options.financialYears = accountYears;
      }
    });
  }, [globalData]);

  React.useEffect(() => {
    if (
      state.filter.values.ratio.length > 0 &&
      state.filter.values.month.length > 0 &&
      (state.filter.values.store.length > 0 ||
        state.filter.values.state.length > 0 ||
        isStoreAdmin) &&
      state.filter.values.financialYear !== null &&
      state.initialLoad === false
    )
      getFilterParams(state.filter.values).then((formData) => {
        getPlAndBsDashboardDetails(formData);
        setState((draft) => {
          draft.initialLoad = true;
        });
      });
  }, [state.filter.values]);

  return {
    state,
    validator,
    isStoreAdmin,
    getChartTitle,
    handleChangeFilter,
    handleApplyFilter,
  };
};
